import type { CSSProperties, HTMLAttributes } from 'react';

import React from 'react';
import { useTranslation } from 'react-i18next';

import { AlertIcon, CloseIcon, PresentationIcon, UploadIcon } from '../../icons';
import { classNames } from '../../utils/class-names';
import { formatFileSize } from '../../utils/format-file-size';
import { IconButton } from '../buttons';
import { Tooltip } from '../tooltip';

import styles from './file-upload-progress.module.css';

export type FileUploadProgressProps = {
  fileData?: string;
  fileName?: string;
  fileSize?: number;
  progress?: number;
  errorMessage?: string;
  testIdPrefix?: string;
  onClose?: () => void;
} & HTMLAttributes<HTMLDivElement>;

export function FileUploadProgress({
  fileData,
  fileName,
  fileSize = 0,
  progress = 0,
  errorMessage,
  testIdPrefix,
  className,
  onClose,
  ...props
}: FileUploadProgressProps) {
  const { t } = useTranslation();

  const fileIsImage = fileData?.startsWith('data:image/');
  const fileIsPresentation = fileData?.startsWith('data:application/');

  return (
    <div
      data-testid={`${testIdPrefix}file-upload-progress`}
      className={classNames(styles.border, errorMessage && styles.hasError, className)}
      {...props}
    >
      <div className={styles.wrapper}>
        <div className={styles.fileInfo}>
          {fileIsPresentation && <PresentationIcon />}

          {fileIsImage && (
            <img
              data-testid={`${testIdPrefix}file-upload-progress-image-preview`}
              src={fileData}
              alt={fileName}
              className={styles.imagePreview}
            />
          )}

          <div className={styles.fileName}>
            <span data-testid={`${testIdPrefix}file-upload-progress-filename`} className={styles.fileName}>
              {fileName}
            </span>

            <span data-testid={`${testIdPrefix}file-upload-progress-file-size`} className={styles.fileSize}>
              {formatFileSize(fileSize)}
            </span>
          </div>

          {onClose && (
            <Tooltip label={t('fileUploader.cancel')}>
              <IconButton
                rounded
                data-testid={`${testIdPrefix}file-upload-progress-close-button`}
                className={styles.closeButton}
                onClick={onClose}
                aria-label={t('fileUploader.cancel')}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>

        {!errorMessage && (
          <div className={styles.progressIndicator}>
            <div className={styles.progressBar} style={{ '--progress': progress } as CSSProperties} />
            <span className={styles.progressValue}>{progress}%</span>
          </div>
        )}

        {errorMessage && (
          <span data-testid={`${testIdPrefix}file-upload-progress-error-message`} className={styles.errorMessage}>
            {errorMessage} <AlertIcon /> <UploadIcon />
          </span>
        )}
      </div>
    </div>
  );
}
